import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import MobileListCard from '../components/MobileListCard';
import EmptyState from '../components/EmptyState';
import moment from 'moment';

const NoticeScreen = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData(1);
  }, []);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `/api/school-common/notices/get-notice-list-student?PageNumber=${page}&PageSize=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const newData = response.data.data || [];

      if (page === 1) {
        setItems(newData);
      } else {
        setItems((prev) => [...prev, ...newData]);
      }

      setPageNumber(page + 1);
      setHasMore(newData.length === 10);
    } catch (error) {
      console.error('NoticeScreen loadData error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to load notices. Please check your connection and try again.';
      setError(errorMessage);
      handleCatch(error);
      if (page === 1) setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setPageNumber(1);
    setHasMore(true);
    await loadData(1);
  }, []);

  const onDownload = (fileUrl) => {
    if (!fileUrl) {
      alert('No file available to download');
      return;
    }
    window.open(fileUrl, '_blank');
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadData(pageNumber);
    }
  };

  const retryLoadData = () => {
    setPageNumber(1);
    setHasMore(true);
    loadData(1);
  };

  // Error state — no data loaded yet
  if (error && items.length === 0 && !loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <MobileHeader
          title="Notices"
          showBack={true}
          rightAction={
            <IconButton onClick={retryLoadData} sx={{ color: '#fff' }}>
              <RefreshIcon />
            </IconButton>
          }
        />
        <EmptyState
          icon={<NotificationsIcon />}
          title="Failed to Load"
          subtitle={error}
          actionLabel="Retry"
          onAction={retryLoadData}
          iconColor="#E53935"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Notices"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', backgroundColor: '#F5F7FA' }}>
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress sx={{ color: colors.BLUE }} />
          </Box>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<NotificationsIcon />}
            title="No Notices"
            subtitle="There are no notices posted yet."
          />
        ) : (
          <Box sx={{ backgroundColor: '#fff', mt: 1, borderRadius: '12px 12px 0 0', mx: 1, overflow: 'hidden' }}>
            {items.map((item, index) => (
              <MobileListCard
                key={index}
                icon={<NotificationsIcon />}
                iconColor="#0066CC"
                title={item.title}
                subtitle={`${item.uploadedBy || ''}  •  ${moment(item.updatedDate).format('DD MMM YYYY')}`}
                description={item.description}
                showChevron={false}
                rightIcon={
                  item.file1?.length > 0 ? (
                    <IconButton
                      size="small"
                      onClick={() => onDownload(item.file1)}
                      sx={{ color: '#0066CC', minHeight: 36, minWidth: 36 }}
                    >
                      <DownloadIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  ) : null
                }
                showDivider={index < items.length - 1}
              />
            ))}

            {/* Load more / spinner at bottom */}
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: colors.BLUE }} />
                ) : (
                  <Box
                    onClick={loadMore}
                    sx={{
                      py: 1,
                      px: 3,
                      fontSize: '13px',
                      color: colors.BLUE,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Load more
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NoticeScreen;
